import {
  getField,
  updateField
} from "vuex-map-fields";
import moment from 'moment';

import {
  updateStateProps,
  mutateStateProps
} from "@/store/common-functions";

import {
  ACTION_RESET_PHARMACY_ORDER,
  ACTION_UPDATE_PHARMACY_ORDER,
  GETTER_PRESCRIPTION_HAS_VALID_COUPON,
  GETTER_PHARMACY_ORDER_PARTNER,
  GETTER_PHARMACY_ORDER_IS_BLOCKED,
  GETTER_PHARMACY_ORDER_SUBMISSION_BODY,
  GETTER_MEDICINE_PARTNER,
  MUTATE_ADD_ATTACHED_ID,
  MUTATE_ADD_ATTACHED_PRESCRIPTION,
  MUTATE_ADD_ORDER_DATA_ITEM,
  MUTATE_PHARMACY_ORDER,
  MUTATE_REMOVE_ATTACHED_ID,
  MUTATE_REMOVE_ATTACHED_PRESCRIPTION,
  MUTATE_REMOVE_ORDER_DATA_ITEM,
  MUTATE_UPDATE_ATTACHED_PRESCRIPTION_REMARKS,
  MUTATE_ORDER_ATTACHED_PRESCRIPTION_INCLUDE_OTHER_ITEMS,
  MUTATE_UPDATE_ORDER_DATA_ITEM
} from "@/store/types";
import {
  isCouponExpired,
  getPrescriptionCoupon,
  canAvailCoupon,
} from '@/utils/medicine';
import {
  COUPON_FULFILLMENT_MODE,
} from '@/reference/medicine';
import {
  isSaxendaProgramId,
} from '@/reference/coupon';

const defaultState = () => ({
  attachedIds: [],
  uploadedIds: [],
  attachedPrescriptions: [],
  uploadedPrescriptions: [],
  availmentCode: null,
  birthdate: '',
  deliveryaddress: {
    buildingname: '',
    city: '',
    roomnumber: '',
    street: '',
  },
  email: '',
  endorsementId: null,
  firstname: '',
  lastname: '',
  fulfillmentmode: COUPON_FULFILLMENT_MODE.DELIVERY,
  gender: 0,
  ids: [],
  idFileView: '',
  mobilenumber: '',
  middlename: '',
  orderid: null,
  orderData: [
    {
      quantity: '',
      name: '',
      form: '',
      remarks: ''
    }
  ],
  partnercode: null,
  prescriptionFileView: '',
  prescriptionView: '',
  selectedBranch: null,
  submittedCouponRedemptionCodes: [],
  termsAgreed: true,
  novoTermsAgreed: true, // Novo Nordisk | Saxenda | WeighNow
  opt_in: {
    educare: true, // Novo Nordisk | Saxenda | WeighNow
  },
});

const state = defaultState();

const getters = {
  getField,
  [GETTER_PRESCRIPTION_HAS_VALID_COUPON](state) {
    if (Array.isArray(state.attachedPrescriptions)) {
      return state.attachedPrescriptions.some((prescription) => {
        const coupon = getPrescriptionCoupon(prescription);

        return coupon ? canAvailCoupon(coupon) && !isCouponExpired(coupon) : false;
      });
    }

    return false;
  },
  [GETTER_PHARMACY_ORDER_PARTNER]({partnercode}, getters) {
    if (!partnercode) return null;

    return getters[GETTER_MEDICINE_PARTNER](partnercode);
  },
  [GETTER_PHARMACY_ORDER_IS_BLOCKED](state, getters) {
    const partner = getters[GETTER_PHARMACY_ORDER_PARTNER];
    const blockedDates = partner ? partner.blockeddates : [];
    const now = moment.utc();

    return blockedDates.some((date) => {
      const hourDiff = now.diff(moment.utc(date), 'hours');
      return hourDiff > 0 && hourDiff <= 24;
    });
  },
  [GETTER_PHARMACY_ORDER_SUBMISSION_BODY](state, getters) {
    const body = {
      coupons: getters[GETTER_PRESCRIPTION_HAS_VALID_COUPON]
        ? state.uploadedPrescriptions
          .filter((uploadedPrescription) => {
            const coupon = getPrescriptionCoupon(uploadedPrescription);
            return coupon && canAvailCoupon(coupon) && !isCouponExpired(coupon);
          })
          .map((uploadedPrescription) => {
            const coupon = getPrescriptionCoupon(uploadedPrescription);

            return {
              couponid: coupon.couponid,
              fileid: uploadedPrescription.uploadid,
              source: uploadedPrescription.source,
              availmentcode: state.availmentCode,
            };
          })
        : [],
      firstname: state.firstname,
      fulfillmentmode: state.fulfillmentmode,
      lastname: state.lastname,
      mobilenumber: state.mobilenumber,
      partnercode: state.partnercode,
      ids: state.uploadedIds,
      prescriptions: state.uploadedPrescriptions
        .map((uploadedPrescription) => ({
          source: uploadedPrescription.source,
          uploadid: uploadedPrescription.uploadid,
          partial: uploadedPrescription.partial,
          remarks: uploadedPrescription.remarks,
        })),
    };

    const addressProp = getters[GETTER_PRESCRIPTION_HAS_VALID_COUPON] ? 'deliveryaddress' : 'address';

    body[addressProp] = state.deliveryaddress;
    body.orderData = state.orderData
      .reduce((finalOrderData, otherItem) => {
        if (otherItem.name && otherItem.quantity) {
          finalOrderData.push({
            name: otherItem.name,
            quantity: parseInt(
              (otherItem.quantity || '').match(/(\d+)/)?.[0],
              10,
            ) || 0,
            form:
              (otherItem.quantity || '').split(' ')?.[1] ||
              otherItem.quantity,
            remarks: otherItem.quantity,
          });
        }

        return finalOrderData;
      }, []);

    if (getters[GETTER_PRESCRIPTION_HAS_VALID_COUPON]) {
      const {programid} = getPrescriptionCoupon(state.attachedPrescriptions[0])

      if (isSaxendaProgramId(programid)) {
        body.opt_in = {
          educare: state.opt_in.educare,
        };
      }
    }

    return body;
  },
};

const actions = {
  [ACTION_RESET_PHARMACY_ORDER]({commit}) {
    commit(MUTATE_PHARMACY_ORDER, defaultState());
  },
  [ACTION_UPDATE_PHARMACY_ORDER]: updateStateProps(MUTATE_PHARMACY_ORDER),
};

const mutations = {
  [MUTATE_ADD_ATTACHED_PRESCRIPTION]: (state, file) => {
    state.attachedPrescriptions.push(file)
  },
  [MUTATE_ADD_ATTACHED_ID]: (state, file) => {
    state.attachedIds.push(file)
  },
  [MUTATE_ADD_ORDER_DATA_ITEM]: (state) => {
    state.orderData.push({
      quantity: '',
      name: '',
      form: '',
      remarks: ''
    })
  },
  [MUTATE_REMOVE_ATTACHED_PRESCRIPTION]: (state, index) => {
    state.attachedPrescriptions.splice(index, 1);
  },
  [MUTATE_REMOVE_ATTACHED_ID]: (state, index) => {
    state.attachedIds.splice(index, 1);
  },
  [MUTATE_REMOVE_ORDER_DATA_ITEM]: (state, index) => {
    state.orderData.splice(index, 1);
  },
  [MUTATE_UPDATE_ATTACHED_PRESCRIPTION_REMARKS]: (state, {index, partial, remarks}) => {
    state.attachedPrescriptions[index].partial = partial;
    state.attachedPrescriptions[index].remarks = remarks;
  },
  [MUTATE_UPDATE_ORDER_DATA_ITEM]: (state, {index, orderDataItem}) => {
    state.orderData[index].name = orderDataItem.name;
    state.orderData[index].quantity = orderDataItem.quantity;
  },
  [MUTATE_ORDER_ATTACHED_PRESCRIPTION_INCLUDE_OTHER_ITEMS]: (state, {index, includeOtherItems}) => {
    state.attachedPrescriptions[index].includeOtherItems = includeOtherItems;
    state.attachedPrescriptions[index].partial = includeOtherItems;
    state.attachedPrescriptions[index].remarks = '';
  },
  [MUTATE_PHARMACY_ORDER]: mutateStateProps,
  updateField
};

export default {
  actions,
  getters,
  mutations,
  state,
};